/**
 * Shared formatting helpers for the violations surface. Mirrors
 * `compliance/lib.tsx`.
 */

import {
  VIOLATION_AGENCY_LABELS,
  VIOLATION_SEVERITY_LABELS,
  VIOLATION_STATUS_LABELS,
  type ViolationAgency,
  type ViolationSeverity,
  type ViolationStatus,
} from '@sitewise/shared';

const DAY_MS = 24 * 60 * 60 * 1000;

export function agencyLabel(a: ViolationAgency): string {
  return VIOLATION_AGENCY_LABELS[a];
}

export function severityLabel(s: ViolationSeverity): string {
  return VIOLATION_SEVERITY_LABELS[s];
}

export function statusLabel(s: ViolationStatus): string {
  return VIOLATION_STATUS_LABELS[s];
}

/** Short date — defers to the user's locale for readability. */
export function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatDateTime(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * "in 3 days" / "5 days ago" / "today" etc. Used by the cure-by
 * countdown.
 */
export function formatRelativeDays(value: string | null, now: Date = new Date()): string {
  if (!value) return '—';
  const diffMs = new Date(value).getTime() - now.getTime();
  const days = Math.round(diffMs / DAY_MS);
  if (days === 0) return 'today';
  if (days > 0) return days === 1 ? 'in 1 day' : `in ${days} days`;
  const abs = Math.abs(days);
  return abs === 1 ? '1 day ago' : `${abs} days ago`;
}

/** Human-readable interval — e.g. "30m", "2h", "1d". */
export function formatPollInterval(ms: number): string {
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}

/** Wrap a money value with currency code. */
export function formatMoney(amount: number | null, currency: string = 'USD'): string {
  if (amount == null) return '—';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(amount);
}
